// The organisation's people: who is in it and with which role, pending invitations and new ones.
// Better Auth's organisation endpoints do the work and check the role's permissions (auth.ts).
import { queryOptions, useQuery, useQueryClient } from '@tanstack/react-query';
import { type FormEvent, useCallback, useRef, useState } from 'react';
import { Form } from 'react-aria-components';
import { useTranslation } from 'react-i18next';
import { ApiError, can, meQuery, type Role } from '../api';
import { authClient } from '../auth-client';
import { Button, Choice, ConfirmButton, Field, Message, Page } from '../ui';
import { readForm } from '../util';
import { OrganizationNav } from './org-nav';

const ROLES: Role[] = ['owner', 'admin', 'editor', 'viewer'];

const membersQuery = (organizationId: string) =>
  queryOptions({
    queryKey: ['organization', organizationId, 'members'],
    queryFn: async () => {
      const { data, error } = await authClient.organization.listMembers();
      if (error) throw new ApiError(error.status ?? 500);
      return data.members;
    },
  });

const invitationsQuery = (organizationId: string) =>
  queryOptions({
    queryKey: ['organization', organizationId, 'invitations'],
    queryFn: async () => {
      const { data, error } = await authClient.organization.listInvitations();
      if (error) throw new ApiError(error.status ?? 500);
      return data.filter((invitation) => invitation.status === 'pending');
    },
  });

export function MembersPage() {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const { data: me } = useQuery(meQuery);
  const organizationId = me?.organization?.id ?? '';
  const members = useQuery({ ...membersQuery(organizationId), enabled: Boolean(organizationId) });
  const invitations = useQuery({ ...invitationsQuery(organizationId), enabled: Boolean(organizationId) });
  const [notice, setNotice] = useState('');
  const [error, setError] = useState<string | null>(null);
  // The invitation form only exists while someone is being added, so the list stays the focus of the page.
  const [inviting, setInviting] = useState(false);
  const addButton = useRef<HTMLButtonElement>(null);
  // Where the focus goes when the control that had it disappears (a removed person, a cancelled
  // invitation): the heading of the section, instead of the top of the page.
  const peopleHeading = useRef<HTMLHeadingElement>(null);
  // The form's heading takes the focus when the form opens (a stable callback: it runs on mount only).
  const focusOnMount = useCallback((element: HTMLElement | null) => element?.focus(), []);
  // The route only renders with an organisation (see router.tsx).
  if (!me?.organization) return null;
  const { organization } = me;
  const mayUpdate = can(me, 'member', 'update');
  const mayRemove = can(me, 'member', 'delete');
  const mayInvite = can(me, 'invitation', 'create');
  const mayCancel = can(me, 'invitation', 'cancel');
  const roleName = (role: string) => t(`home.roles.${role}`, { defaultValue: role });
  // Only an owner hands out (or takes away) the owner role.
  const assignable = ROLES.filter((role) => role !== 'owner' || organization.role === 'owner');

  async function run(
    action: () => Promise<{ error: unknown }>,
    done: string,
    focus?: () => HTMLElement | null,
  ) {
    setError(null);
    setNotice('');
    const { error: failure } = await action();
    if (failure) {
      setError(t('members.errors.generic'));
      return false;
    }
    await queryClient.invalidateQueries({ queryKey: ['organization', organizationId] });
    setNotice(done);
    focus?.()?.focus();
    return true;
  }

  async function invite(event: FormEvent<HTMLFormElement>) {
    const form = event.currentTarget;
    const { email = '', role = 'editor' } = readForm(event);
    setError(null);
    setNotice('');
    const { error: failure } = await authClient.organization.inviteMember({
      email: email.trim(),
      role: role as 'admin',
    });
    if (failure) {
      setError(
        /already/i.test(failure.code ?? '') ? t('members.errors.already') : t('members.errors.generic'),
      );
      return;
    }
    form.reset();
    closeInvite();
    await queryClient.invalidateQueries({ queryKey: ['organization', organizationId, 'invitations'] });
    setNotice(t('members.invited', { email: email.trim() }));
  }

  function closeInvite() {
    setInviting(false);
    addButton.current?.focus();
  }

  const date = (iso: Date | string) =>
    new Intl.DateTimeFormat(i18n.language, { dateStyle: 'long' }).format(new Date(iso));

  return (
    <Page title={t('members.title', { name: organization.name })} wide>
      <OrganizationNav />
      <p role="status" className="empty:hidden">
        {notice}
      </p>
      {error && <Message tone="error">{error}</Message>}

      <section aria-labelledby="personas" className="team-panel mt-4 flex flex-col gap-4">
        <h2 id="personas" ref={peopleHeading} tabIndex={-1} className="text-2xl font-bold">
          {t('members.membersTitle')}
        </h2>
        {members.isPending ? (
          <p>{t('app.loading')}</p>
        ) : members.isError ? (
          <Message tone="error">{t('errors.generic')}</Message>
        ) : (
          // On a narrow screen the table scrolls: keyboards reach it, and its name says what it holds.
          // biome-ignore lint/a11y/noNoninteractiveTabindex: a scrolling area must be reachable by keyboard.
          <section className="overflow-x-auto" aria-label={t('members.tableLabel')} tabIndex={0}>
            <table className="w-full border-collapse text-left">
              <thead>
                <tr className="border-b-2 border-line">
                  <th scope="col" className="py-2 pr-4">
                    {t('members.name')}
                  </th>
                  <th scope="col" className="py-2 pr-4">
                    {t('members.email')}
                  </th>
                  <th scope="col" className="py-2 pr-4">
                    {t('members.role')}
                  </th>
                  {mayRemove && (
                    <th scope="col" className="py-2">
                      {t('members.actions')}
                    </th>
                  )}
                </tr>
              </thead>
              <tbody>
                {members.data.map((member) => {
                  const self = member.userId === me.user.id;
                  const name = member.user.name || member.user.email;
                  const editable =
                    mayUpdate && !self && (member.role !== 'owner' || organization.role === 'owner');
                  return (
                    <tr key={member.id} className="border-b border-line align-middle">
                      <td className="py-2 pr-4">
                        {name} {self && <span className="text-ink-muted">{t('members.you')}</span>}
                      </td>
                      <td className="py-2 pr-4 wrap-anywhere">{member.user.email}</td>
                      <td className="py-2 pr-4">
                        {editable ? (
                          <>
                            <label htmlFor={`rol-${member.id}`} className="sr-only">
                              {t('members.roleOf', { name })}
                            </label>
                            <select
                              id={`rol-${member.id}`}
                              value={member.role}
                              onChange={(event) => {
                                const role = event.target.value;
                                void run(
                                  () =>
                                    authClient.organization.updateMemberRole({
                                      memberId: member.id,
                                      role: role as 'admin',
                                    }),
                                  t('members.roleSaved', { name, role: roleName(role) }),
                                );
                              }}
                              className="min-h-11 rounded-md border-2 border-field-border bg-paper px-2 text-ink"
                            >
                              {assignable.map((role) => (
                                <option key={role} value={role}>
                                  {roleName(role)}
                                </option>
                              ))}
                            </select>
                          </>
                        ) : (
                          roleName(member.role)
                        )}
                      </td>
                      {mayRemove && (
                        <td className="py-2">
                          {!self && member.role !== 'owner' && (
                            <ConfirmButton
                              label={t('members.remove')}
                              hidden={name}
                              title={t('members.removeTitle', { name })}
                              body={t('members.removeIntro')}
                              confirm={t('members.removeConfirm')}
                              cancel={t('members.cancel')}
                              onConfirm={async () => {
                                await run(
                                  () => authClient.organization.removeMember({ memberIdOrEmail: member.id }),
                                  t('members.removed', { name }),
                                  () => peopleHeading.current,
                                );
                              }}
                            />
                          )}
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </section>
        )}
        {mayInvite && (
          <div>
            <Button
              ref={addButton}
              variant="secondary"
              aria-expanded={inviting}
              aria-controls={inviting ? 'invitar' : undefined}
              onPress={() => (inviting ? closeInvite() : setInviting(true))}
            >
              {t('members.add')}
            </Button>
          </div>
        )}
      </section>

      {mayInvite && inviting && (
        <section
          aria-labelledby="invitar-titulo"
          id="invitar"
          className="team-panel mt-6 flex max-w-2xl flex-col gap-4"
        >
          <h2 id="invitar-titulo" ref={focusOnMount} tabIndex={-1} className="text-2xl font-bold">
            {t('members.inviteTitle')}
          </h2>
          <p>{t('members.inviteIntro')}</p>
          <Form onSubmit={invite} validationBehavior="native" className="flex flex-col gap-6">
            <Field name="email" type="email" label={t('members.inviteEmail')} autoComplete="off" isRequired />
            <Choice
              name="role"
              label={t('members.inviteRole')}
              options={assignable.map((role) => ({
                value: role,
                label: roleName(role),
                hint: t(`members.roleHints.${role}`),
              }))}
              defaultValue="editor"
            />
            <div className="flex flex-wrap gap-3">
              <Button type="submit">{t('members.inviteSubmit')}</Button>
              <Button variant="secondary" onPress={closeInvite}>
                {t('members.cancel')}
              </Button>
            </div>
          </Form>
        </section>
      )}

      {invitations.data && invitations.data.length > 0 && (
        <section aria-labelledby="invitaciones" className="team-panel mt-10 flex flex-col gap-4">
          <h2 id="invitaciones" className="text-2xl font-bold">
            {t('members.invitationsTitle')}
          </h2>
          <ul className="flex flex-col gap-3">
            {invitations.data.map((invitation) => (
              <li key={invitation.id} className="flex flex-wrap items-center gap-3">
                <span className="wrap-anywhere">
                  {t('members.invitationItem', {
                    email: invitation.email,
                    role: roleName(invitation.role ?? ''),
                    date: date(invitation.expiresAt),
                  })}
                </span>
                {mayCancel && (
                  <Button
                    variant="secondary"
                    onPress={() =>
                      run(
                        () => authClient.organization.cancelInvitation({ invitationId: invitation.id }),
                        t('members.invitationCancelled', { email: invitation.email }),
                        () => peopleHeading.current,
                      )
                    }
                  >
                    {t('members.cancelInvitation')}
                    <span className="sr-only"> {invitation.email}</span>
                  </Button>
                )}
              </li>
            ))}
          </ul>
        </section>
      )}
    </Page>
  );
}
